/**
 * Notification Filter Utility
 * 
 * Handles filtering of notifications based on user preferences including:
 * - Category preferences (system, tool, admin, security)
 * - Do Not Disturb mode with time range
 */

import { UserPreferences } from '@/app/types/auth';
import { Notification } from '../types/notification.types';

/**
 * Check if current time is within Do Not Disturb period
 */
export function isInDoNotDisturbPeriod(
  dndEnabled: boolean,
  dndStartTime?: string,
  dndEndTime?: string
): boolean {
  if (!dndEnabled || !dndStartTime || !dndEndTime) {
    return false;
  }

  try {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Minutes since midnight

    // Parse start and end times (format: "HH:mm")
    const [startHour, startMin] = dndStartTime.split(':').map(Number);
    const [endHour, endMin] = dndEndTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    // Handle overnight periods (e.g., 22:00 to 08:00)
    if (startMinutes > endMinutes) {
      return currentTime >= startMinutes || currentTime < endMinutes;
    }
    
    // Normal same-day period
    return currentTime >= startMinutes && currentTime < endMinutes;
  } catch (error) {
    console.error('[NotificationFilter] Error checking DND period:', error);
    return false;
  }
}

/**
 * Map notification category to preference key
 */
function getCategoryPreferenceKey(category?: string): keyof UserPreferences | null {
  console.log('[NotificationFilter] getCategoryPreferenceKey called:', {
    category,
    categoryType: typeof category,
    categoryIsUndefined: category === undefined,
    categoryIsNull: category === null,
    categoryIsEmpty: category === '',
    categoryLength: category?.length
  });
  
  if (!category) {
    console.log('[NotificationFilter] getCategoryPreferenceKey: No category, returning null');
    return null;
  }
  
  const categoryLower = category.toLowerCase().trim();
  console.log('[NotificationFilter] getCategoryPreferenceKey: Category lowercase:', {
    original: category,
    lowercase: categoryLower,
    trimmed: categoryLower === category.toLowerCase()
  });
  
  // Map category strings to preference keys
  const categoryMap: Record<string, keyof UserPreferences> = {
    'system': 'notificationSystemEnabled',
    'tool': 'notificationToolEnabled',
    'admin': 'notificationAdminEnabled',
    'security': 'notificationSecurityEnabled',
  };
  
  const result = categoryMap[categoryLower] || null;
  console.log('[NotificationFilter] getCategoryPreferenceKey: Result:', {
    categoryLower,
    result,
    availableKeys: Object.keys(categoryMap),
    matchFound: !!result
  });
  
  return result;
}

/**
 * Check if notification category is enabled in user preferences
 */
export function isCategoryEnabled(
  notification: Notification,
  preferences: UserPreferences | null
): boolean {
  // If no preferences, allow all notifications
  if (!preferences) {
    console.log('[NotificationFilter] isCategoryEnabled: No preferences, allowing');
    return true;
  }

  const category = notification.notificationCategory;
  console.log('[NotificationFilter] isCategoryEnabled: Checking category:', {
    category,
    categoryType: typeof category,
    categoryValue: JSON.stringify(category),
    categoryIsUndefined: category === undefined,
    categoryIsNull: category === null,
    categoryIsEmptyString: category === '',
    rawNotification: notification
  });
  
  const preferenceKey = getCategoryPreferenceKey(category);
  
  console.log('[NotificationFilter] isCategoryEnabled: Preference key lookup result:', {
    preferenceKey,
    category,
    allPreferences: {
      notificationSystemEnabled: preferences.notificationSystemEnabled,
      notificationToolEnabled: preferences.notificationToolEnabled,
      notificationAdminEnabled: preferences.notificationAdminEnabled,
      notificationSecurityEnabled: preferences.notificationSecurityEnabled
    }
  });
  
  // If category is not recognized or no preference key, allow notification
  if (!preferenceKey) {
    console.log('[NotificationFilter] isCategoryEnabled: No preference key found, allowing by default');
    return true;
  }
  
  // Check if the category is enabled (default to true if not set)
  const isEnabled = preferences[preferenceKey];
  console.log('[NotificationFilter] isCategoryEnabled: Final decision:', {
    preferenceKey,
    isEnabled,
    isEnabledType: typeof isEnabled,
    isEnabledValue: JSON.stringify(isEnabled),
    willAllow: isEnabled !== false,
    reasoning: isEnabled === false ? 'BLOCKED - preference is explicitly false' : 
               isEnabled === true ? 'ALLOWED - preference is explicitly true' :
               'ALLOWED - preference is undefined/null (default allow)'
  });
  
  return isEnabled !== false; // Allow if undefined or true
}

/**
 * Check if notification should be shown based on all filters
 */
export function shouldShowNotification(
  notification: Notification,
  preferences: UserPreferences | null
): boolean {
  console.log('[NotificationFilter] shouldShowNotification called:', {
    notificationId: notification.id,
    notificationTitle: notification.notificationTitle,
    notificationCategory: notification.notificationCategory,
    hasPreferences: !!preferences,
    preferences: preferences ? {
      notificationSystemEnabled: preferences.notificationSystemEnabled,
      notificationToolEnabled: preferences.notificationToolEnabled,
      notificationAdminEnabled: preferences.notificationAdminEnabled,
      notificationSecurityEnabled: preferences.notificationSecurityEnabled,
      notificationDndEnabled: preferences.notificationDndEnabled,
      notificationDndStartTime: preferences.notificationDndStartTime,
      notificationDndEndTime: preferences.notificationDndEndTime
    } : null
  });

  // If no preferences, show all notifications
  if (!preferences) {
    console.log('[NotificationFilter] No preferences found, allowing notification');
    return true;
  }

  // Check Do Not Disturb mode
  const inDndPeriod = isInDoNotDisturbPeriod(
    preferences.notificationDndEnabled ?? false,
    preferences.notificationDndStartTime,
    preferences.notificationDndEndTime
  );
  
  if (inDndPeriod) {
    console.log('[NotificationFilter] Notification blocked by Do Not Disturb mode:', {
      notificationId: notification.id,
      category: notification.notificationCategory,
      dndEnabled: preferences.notificationDndEnabled,
      dndStartTime: preferences.notificationDndStartTime,
      dndEndTime: preferences.notificationDndEndTime
    });
    return false;
  }

  // Check category preferences
  const categoryAllowed = isCategoryEnabled(notification, preferences);
  
  if (!categoryAllowed) {
    console.log('[NotificationFilter] Notification blocked by category preference:', {
      notificationId: notification.id,
      category: notification.notificationCategory,
      preferences: {
        system: preferences.notificationSystemEnabled,
        tool: preferences.notificationToolEnabled,
        admin: preferences.notificationAdminEnabled,
        security: preferences.notificationSecurityEnabled
      }
    });
    return false;
  }

  console.log('[NotificationFilter] Notification allowed');
  return true;
}

/**
 * Check if toast should be shown for notification
 */
export function shouldShowToast(
  notification: Notification,
  preferences: UserPreferences | null
): boolean {
  // First check if notification should be shown at all
  if (!shouldShowNotification(notification, preferences)) {
    return false;
  }

  // Check if toast is enabled
  if (preferences && preferences.notificationToastEnabled === false) {
    return false;
  }

  return true;
}

/**
 * Check if sound should be played for notification
 */
export function shouldPlaySound(
  notification: Notification,
  preferences: UserPreferences | null
): boolean {
  // First check if notification should be shown at all
  if (!shouldShowNotification(notification, preferences)) {
    return false;
  }

  // Check if sound is enabled
  if (preferences && preferences.notificationSoundEnabled === false) {
    return false;
  }

  return true;
}
